import React, { useCallback, useEffect, useState } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { db } from "services/firebase";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useForm, Controller } from "react-hook-form";
import { format } from "date-fns";
import ConfirmDialog from "components/common/ConfirmDialog";

/**
 * EdClaimApprovalSection — admin review queue for CareMap-1 facility claims.
 *
 * Lists pending `edLocationClaims` and lets an admin approve (grants membership via
 * the reviewEdLocationClaim callable, which writes locations/{id}.users) or reject
 * with a reason. All writes go through the callable — the client never touches
 * edLocationClaims or locations.users directly.
 */
const EdClaimApprovalSection = () => {
  const [claims, setClaims] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [rowLoading, setRowLoading] = useState(null); // `${claimId}:approve|reject`
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [rejectTarget, setRejectTarget] = useState(null);

  const { control, handleSubmit, reset } = useForm({ defaultValues: { reason: "" } });

  const fetchClaims = useCallback(async () => {
    setLoaded(false);
    try {
      const snap = await db
        .collection("edLocationClaims")
        .where("status", "==", "pending")
        .get();
      const rows = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      rows.sort((a, b) => {
        const at = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const bt = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return bt - at;
      });
      setClaims(rows);
    } catch (err) {
      console.warn("[EdClaimApprovalSection] fetch failed:", err);
      setError(err?.message || "Could not load facility claims.");
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  const review = useCallback(
    async (claimId, decision, reason) => {
      setRowLoading(`${claimId}:${decision === "approved" ? "approve" : "reject"}`);
      setError("");
      setSuccess("");
      try {
        const fn = firebase.functions().httpsCallable("reviewEdLocationClaim");
        await fn({ claimId, decision, reason });
        setClaims((prev) => prev.filter((c) => c.id !== claimId));
        setSuccess(
          decision === "approved"
            ? "Claim approved — the clinician now has facility access."
            : "Claim rejected."
        );
      } catch (err) {
        console.warn("[EdClaimApprovalSection] review failed:", err);
        setError(err?.message || "Could not update the claim.");
      } finally {
        setRowLoading(null);
      }
    },
    []
  );

  const onConfirmReject = handleSubmit(async ({ reason }) => {
    if (!rejectTarget) return;
    await review(rejectTarget.id, "rejected", reason.trim());
    setRejectTarget(null);
    reset({ reason: "" });
  });

  const formatSubmitted = (createdAt) => {
    if (!createdAt?.toDate) return "—";
    try {
      return format(createdAt.toDate(), "PP");
    } catch {
      return "—";
    }
  };

  return (
    <Box sx={{ mt: 6 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Facility Claims
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Clinicians claiming unclaimed CareMap listings. Approving grants the claimant
        facility access; rejecting records a reason.
      </Typography>

      {success ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      ) : null}
      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      {!loaded ? (
        <CircularProgress size={30} />
      ) : claims.length === 0 ? (
        <Alert severity="info">No pending facility claims.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="facility claims table">
            <TableHead>
              <TableRow>
                <TableCell>Facility</TableCell>
                <TableCell>Claimant</TableCell>
                <TableCell>NPI</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {claims.map((claim) => (
                <TableRow key={claim.id}>
                  <TableCell>
                    {claim.locationTitle || claim.locationId}
                    {claim.domainVerificationId ? (
                      <Chip
                        size="small"
                        color="success"
                        label="domain verified"
                        sx={{ ml: 1 }}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell>{claim.claimantEmail || claim.claimantUid}</TableCell>
                  <TableCell>{claim.npi}</TableCell>
                  <TableCell>{formatSubmitted(claim.createdAt)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <LoadingButton
                        size="small"
                        variant="contained"
                        loading={rowLoading === `${claim.id}:approve`}
                        disabled={!!rowLoading}
                        onClick={() => review(claim.id, "approved")}
                      >
                        Approve
                      </LoadingButton>
                      <LoadingButton
                        size="small"
                        color="error"
                        loading={rowLoading === `${claim.id}:reject`}
                        disabled={!!rowLoading}
                        onClick={() => {
                          reset({ reason: "" });
                          setRejectTarget(claim);
                        }}
                      >
                        Reject
                      </LoadingButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        title="Reject facility claim"
        confirmLabel="Reject claim"
        confirmColor="error"
        loading={rowLoading === `${rejectTarget?.id}:reject`}
        onConfirm={onConfirmReject}
        onExited={() => reset({ reason: "" })}
        message={
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Rejecting the claim on{" "}
              <strong>{rejectTarget?.locationTitle || rejectTarget?.locationId}</strong>.
              The reason is stored on the claim record.
            </Typography>
            <Controller
              name="reason"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Reason (optional)"
                  multiline
                  minRows={2}
                  fullWidth
                />
              )}
            />
          </Stack>
        }
      />
    </Box>
  );
};

export default EdClaimApprovalSection;
